import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';

import { ROLES } from '../../firebase.marketplace';
import { getUserRoles, hasRole, getArtistRequestStatus } from '../../services/marketplaceAuthService';
import * as marketplaceAuthService from '../../services/marketplaceAuthService';
import { 
  Tooltip, 
  Menu, 
  MenuItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Badge
} from '@mui/material';
import { 
  AccountCircle, 
  Login as LoginIcon, 
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as ProfileIcon,
  AdminPanelSettings as AdminIcon,
  Palette as PaletteIcon,
  Security,
  Devices as DevicesIcon,
  CheckCircle,
  HourglassEmpty,
  ShoppingCart,
  Favorite,
  Dashboard,
  Store as StoreIcon,
  Search as SearchIcon,
  AttachMoney as AttachMoneyIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  position: relative;
  z-index: 10;
  
  @media (max-width: 768px) {
    gap: 0.7rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const DashboardButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(90deg, rgba(76, 175, 80, 0.2), rgba(76, 175, 80, 0.3));
  color: white;
  border: 1px solid rgba(76, 175, 80, 0.5);
  padding: 0.6rem 1.2rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.25);
  font-weight: 600;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  svg {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
    font-size: 1.2rem;
  }
  
  &:hover {
    background: linear-gradient(90deg, rgba(76, 175, 80, 0.3), rgba(76, 175, 80, 0.5));
    box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4);
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0.45rem 0.7rem;
    font-size: 0.85rem;
    
    .button-text {
      display: none;
    }
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: ${props => props.primary ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'rgba(255, 255, 255, 0.05)'};
  color: white;
  border: ${props => props.primary ? 'none' : '1px solid rgba(255, 255, 255, 0.2)'};
  padding: 0.6rem 1.2rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  box-shadow: ${props => props.primary ? '0 4px 15px rgba(74, 144, 226, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.15)'};
  font-weight: ${props => props.primary ? '600' : '500'};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  svg {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
    font-size: 1.2rem;
  }
  
  &:hover {
    background: ${props => props.primary ? 'linear-gradient(90deg, #357abd, #2868a9)' : 'rgba(255, 255, 255, 0.1)'};
    box-shadow: ${props => props.primary ? '0 6px 18px rgba(74, 144, 226, 0.45)' : '0 4px 12px rgba(255, 255, 255, 0.15)'};
    transform: translateY(-2px);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: ${props => props.primary ? '0 2px 8px rgba(74, 144, 226, 0.3)' : '0 1px 4px rgba(255, 255, 255, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0.45rem 0.7rem;
    font-size: 0.85rem;
    border-radius: 18px;
    
    .button-text {
      display: none;
    }
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

const AuthButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(90deg, #4a90e2, #357abd)' : 'transparent'};
  color: white;
  border: ${props => props.primary ? 'none' : '1px solid rgba(74, 144, 226, 0.5)'};
  padding: 0.6rem 1.5rem;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
  box-shadow: ${props => props.primary ? '0 4px 15px rgba(74, 144, 226, 0.35)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  font-weight: ${props => props.primary ? '600' : '500'};
  display: flex;
  align-items: center;
  gap: 0.6rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
  }
  
  svg {
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
    font-size: 1.2rem;
  }

  &:hover {
    background: ${props => props.primary ? 'linear-gradient(90deg, #357abd, #2868a9)' : 'rgba(74, 144, 226, 0.1)'};
    box-shadow: ${props => props.primary ? '0 6px 18px rgba(74, 144, 226, 0.45)' : '0 4px 12px rgba(74, 144, 226, 0.15)'};
    transform: translateY(-2px);
    border-color: ${props => props.primary ? 'none' : 'rgba(74, 144, 226, 0.7)'};
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: ${props => props.primary ? '0 2px 8px rgba(74, 144, 226, 0.3)' : '0 1px 4px rgba(74, 144, 226, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1.2rem;
    font-size: 0.9rem;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0.45rem 0.8rem;
    font-size: 0.85rem;
    border-radius: 18px;
    
    .button-text {
      display: none;
    }
    
    svg {
      font-size: 1.1rem;
    }
  }
`;

const StyledMenu = styled.div`
  .MuiPaper-root {
    background-color: #1a1a1a;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    min-width: 220px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
    overflow: hidden;
  }
  
  .MuiPaper-root::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
  
  .MuiMenuItem-root {
    font-size: 0.95rem;
    padding: 0.6rem 1.2rem;
    transition: all 0.2s ease;
  }
  
  .MuiMenuItem-root .MuiListItemIcon-root {
    min-width: 40px;
    color: white;
  }
  
  .MuiMenuItem-root .MuiListItemText-primary {
    font-weight: 500;
  }
  
  .MuiMenuItem-root:hover {
    background-color: rgba(255, 255, 255, 0.08);
    transform: translateX(4px);
  }
  
  @media (max-width: 480px) {
    .MuiMenuItem-root {
      font-size: 0.9rem;
      padding: 0.5rem 1rem;
    }
    
    .MuiPaper-root {
      min-width: 200px;
      border-radius: 14px;
    }
  }
`;

const AvatarButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${props => props.hasImage ? 'transparent' : 'linear-gradient(135deg, #4a90e2, #357abd)'};
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  border: 2px solid rgba(255, 255, 255, 0.25);
  transition: all 0.3s ease;
  margin-left: 0.5rem;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
    pointer-events: none;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 1rem;
    
    .user-name {
      display: none;
    }
  }
`;

const UserName = styled.span`
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  margin-right: 0.5rem;
  background: linear-gradient(90deg, #fff, #e0e0e0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-right: 0.3rem;
  }
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const BaseBadge = styled.span`
  color: white;
  font-size: 0.75rem;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.6rem;
  cursor: help;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
  letter-spacing: 0.5px;
  font-weight: 600;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
    margin-right: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.15rem 0.4rem;
    font-size: 0.65rem;
    margin-right: 0.3rem;
  }
`;

const SuperAdminBadge = styled(BaseBadge)`
  background: linear-gradient(90deg, #9c27b0, #7b1fa2);
`;

const AdminBadge = styled(BaseBadge)`
  background: linear-gradient(90deg, #ff5722, #e64a19);
`;

const ArtistBadge = styled(BaseBadge)`
  background: linear-gradient(90deg, #4caf50, #388e3c);
`;

const DefaultBadge = styled(BaseBadge)`
  background: linear-gradient(90deg, #4a90e2, #357abd);
`;

const AuthButtons = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState({ [ROLES.USER]: true });
  const [artistRequestStatus, setArtistRequestStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isArtist, setIsArtist] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  


  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          // Fetch user roles
          const userRoles = await getUserRoles(currentUser.uid);
          setRoles(userRoles);
          
          // Set role flags for easier access
          setIsArtist(!!userRoles[ROLES.ARTIST]);
          setIsAdmin(!!userRoles[ROLES.ADMIN]);
          setIsSuperAdmin(!!userRoles[ROLES.SUPER_ADMIN]);
          
          // Check artist request status if not already an artist
          if (!userRoles[ROLES.ARTIST]) {
            try {
              const status = await getArtistRequestStatus(currentUser.uid);
              setArtistRequestStatus(status);
            } catch (error) {
              console.error('Error getting artist request status:', error);
              // Silently fail - don't show error to user
            }
          }
        } catch (error) {
          console.error('Error fetching user roles:', error);
          // Use default roles if there's an error
          setRoles({ [ROLES.USER]: true });
        }
      }
    };
    
    fetchUserData();
  }, [currentUser]);
  
  // No need to fetch cart items count here as it's now handled in MarketplaceLayout

  const handleSignIn = () => {
    navigate('/marketplace/auth/signin');
  };

  const handleSignUp = () => {
    navigate('/marketplace/auth/signup');
  };

  const handleLogout = async () => {
    try {
      // Use the marketplace auth service for logout
      await marketplaceAuthService.signOutUser();
      navigate('/marketplace');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };
  
  const handleProfileMenuOpen = (event) => {
    // Make sure the event.currentTarget is valid before setting it
    if (event && event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
  };
  
  const handleDashboardClick = () => {
    navigate('/marketplace/artist-dashboard');
  };



  if (!currentUser) {
    return (
      <ButtonsContainer>
        <ActionButton onClick={() => navigate('/marketplace/search')}>
          <SearchIcon fontSize="small" />
          Search
        </ActionButton>
        <AuthButton primary onClick={handleSignIn}>
          <LoginIcon fontSize="small" />
          <span className="button-text">Sign In</span>
        </AuthButton>
        <AuthButton onClick={handleSignUp}>
          <AccountCircle fontSize="small" />
          <span className="button-text">Sign Up</span>
        </AuthButton>
      </ButtonsContainer>
    );
  }

  return (
    <ButtonsContainer>
      <ActionButton onClick={() => navigate('/marketplace/search')}>
        <SearchIcon fontSize="small" />
        Search
      </ActionButton>
      
      {isArtist && (
        <ActionButton
          onClick={handleDashboardClick}
        >
          <Dashboard fontSize="small" />
          <span className="button-text">Artist Dashboard</span>
        </ActionButton>
      )}
      
      {/* Cart button moved to MarketplaceLayout */}
      
      {!isArtist && !artistRequestStatus && (
        <ActionButton 
          onClick={() => navigate('/marketplace/become-artist')}
          style={{ background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)', border: 'none' }}
        >
          <PaletteIcon fontSize="small" />
          <span className="button-text">Become an Artist</span>
        </ActionButton>
      )}
      
      {!isArtist && artistRequestStatus === 'pending' && (
        <Tooltip title="Your artist request is being reviewed" arrow>
          <ActionButton 
            onClick={() => navigate('/marketplace/become-artist')}
            style={{ background: 'rgba(255, 152, 0, 0.2)', border: '1px solid #FF9800' }}
          >
            <HourglassEmpty fontSize="small" />
            <span className="button-text">Request Pending</span>
          </ActionButton>
        </Tooltip>
      )}
        
      <UserInfo>
        <AuthButton onClick={handleProfileMenuOpen}>
          <AccountCircle style={{ marginRight: '5px' }} />
          <UserName className="user-name">{currentUser.displayName || currentUser.email}</UserName>
          {isSuperAdmin && (
            <Tooltip title="Super Admin: Full access to all marketplace features" arrow>
              <SuperAdminBadge>Super Admin</SuperAdminBadge>
            </Tooltip>
          )}
          {isAdmin && !isSuperAdmin && (
            <Tooltip title="Admin: Manage artists, content, and marketplace operations" arrow>
              <AdminBadge>Admin</AdminBadge>
            </Tooltip>
          )}
          {isArtist && (
            <Tooltip title="Artist: Upload and manage your music and content" arrow>
              <ArtistBadge>Artist</ArtistBadge>
            </Tooltip>
          )}
        </AuthButton>
        
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            style: {
              backgroundColor: '#0a1128',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
          >
          <MenuItem onClick={() => handleMenuItemClick('/marketplace/profile')}>  
            <ListItemIcon>
              <ProfileIcon fontSize="small" style={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
          
          <MenuItem onClick={() => handleMenuItemClick('/marketplace/orders')}>
            <ListItemIcon>
              <ShoppingCart style={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="My Orders" />
          </MenuItem>
          
          <MenuItem onClick={() => handleMenuItemClick('/marketplace/security/mfa-setup')}>
            <ListItemIcon>
              <Security fontSize="small" style={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Two-Factor Authentication" />
          </MenuItem>
          
          <MenuItem onClick={() => handleMenuItemClick('/marketplace/security/sessions')}>
            <ListItemIcon>
              <DevicesIcon fontSize="small" style={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Manage Sessions" />
          </MenuItem>
          
          {isArtist && (
            <>
              <MenuItem onClick={() => handleMenuItemClick('/marketplace/artist-dashboard')}>
                <ListItemIcon>
                  <StoreIcon fontSize="small" style={{ color: '#4caf50' }} />
                </ListItemIcon>
                <ListItemText primary="Artist Dashboard" />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/marketplace/withdraw')}>
                <ListItemIcon>
                  <AttachMoneyIcon fontSize="small" style={{ color: '#43a047', fontWeight: 'bold' }} />
                </ListItemIcon>
                <ListItemText primary="Withdraw Funds" style={{ fontWeight: 'bold' }} />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/marketplace/payment-credentials')}>
                <ListItemIcon>
                  <PaymentIcon fontSize="small" style={{ color: '#4a90e2' }} />
                </ListItemIcon>
                <ListItemText primary="Payment API Credentials" />
              </MenuItem>
            </>
          )}
          
          {isSuperAdmin && (
            <>
              <MenuItem onClick={() => handleMenuItemClick('/marketplace/admin-dashboard')}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" style={{ color: '#9c27b0' }} />
                </ListItemIcon>
                <ListItemText primary="Super Admin Dashboard" />
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick('/marketplace/artist-approval')}>
                <ListItemIcon>
                  <CheckCircle fontSize="small" style={{ color: '#9c27b0' }} />
                </ListItemIcon>
                <ListItemText primary="Artist Approvals" />
              </MenuItem>
            </>
          )}
          
          {!isArtist && !artistRequestStatus && (
            <MenuItem onClick={() => handleMenuItemClick('/marketplace/become-artist')}>
              <ListItemIcon>
                <PaletteIcon fontSize="small" style={{ color: '#4caf50' }} />
              </ListItemIcon>
              <ListItemText primary="Become an Artist" />
            </MenuItem>
          )}
          
          {!isArtist && artistRequestStatus === 'pending' && (
            <MenuItem onClick={() => handleMenuItemClick('/marketplace/become-artist')}>
              <ListItemIcon>
                <HourglassEmpty fontSize="small" style={{ color: '#ff9800' }} />
              </ListItemIcon>
              <ListItemText primary="Artist Request Pending" />
            </MenuItem>
          )}
            
          <Divider style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
          
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" style={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </Menu>
      </UserInfo>
    </ButtonsContainer>
  );
};

export default AuthButtons;
